/**
 * DateRange Library - Single Source of Truth
 *
 * ARCHITEKTUR-REGELN (VERBINDLICH):
 *
 * 1) Half-Open Ranges: [start, end)
 *    - start <= timestamp < end
 *    - KEINE +1Tag/-1Sekunde Tricks
 *
 * 2) Zeitzone: Europe/Berlin
 *    - Alle Kalenderberechnungen in Berlin
 *    - DST-Wechsel werden korrekt behandelt
 *
 * 3) Begriffe:
 *    - DateRange: { start: Date, end: Date } - UTC Instants für DB-Queries
 *    - BusinessDate: YYYY-MM-DD String in Europe/Berlin
 *
 * Legacy Evidence:
 * - /opt/wash-ops/src/lib/dates.ts (getBerlinDayRange, computeBusinessRange)
 * - Schichtlogik: siehe shift-matcher.ts + ShiftRuleService (DB-basiert)
 */

const BERLIN_TZ = 'Europe/Berlin';

// =============================================================================
// CORE TYPES
// =============================================================================

export interface DateRange {
  /** Start (inclusive) - UTC Instant */
  start: Date;
  /** End (exclusive) - UTC Instant */
  end: Date;
}

export interface BusinessDateRange extends DateRange {
  /** Start als YYYY-MM-DD in Berlin */
  startDate: string;
  /** End als YYYY-MM-DD in Berlin */
  endDate: string;
  /** UI Label */
  label: string;
  /** true wenn Zeitraum noch nicht abgeschlossen */
  isPartial: boolean;
}

export type QuickPicker =
  'today' | 'yesterday' | 'dayBefore' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth';

// =============================================================================
// BERLIN TIMEZONE HELPERS
// =============================================================================

/**
 * Berechnet den UTC-Offset für Berlin (inkl. DST)
 * @returns Offset in Millisekunden (Berlin - UTC)
 */
const BERLIN_TEILE = new Intl.DateTimeFormat('en-US', {
  timeZone: BERLIN_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

function getBerlinOffset(date: Date): number {
  // Die Berliner Wanduhr-Bestandteile ablesen und als UTC zusammensetzen —
  // die Differenz zum echten Zeitpunkt IST der Offset.
  //
  // Der frühere Weg (in Text formatieren, mit `new Date(...)` zurückparsen)
  // hing an der Zeitzone des laufenden Rechners: Der Rückweg interpretiert die
  // Zeichenkette LOKAL. Auf einem UTC-Server ging das gut, auf einem Rechner in
  // Europe/Berlin lieferte er in der März-Umstellungsnacht eine Stunde zu wenig,
  // weil die Hilfszeit selbst in die nicht existierende Stunde fiel (gefunden
  // beim Bau der Schicht-Zeitpunkte, 19.08.2026).
  const teile: Record<string, string> = {};
  for (const t of BERLIN_TEILE.formatToParts(date)) teile[t.type] = t.value;
  const alsWaereBerlinUtc = Date.UTC(
    Number(teile.year),
    Number(teile.month) - 1,
    Number(teile.day),
    Number(teile.hour) % 24,
    Number(teile.minute),
    Number(teile.second),
  );
  return alsWaereBerlinUtc - date.getTime();
}

/**
 * Berliner Wanduhrzeit → echter Zeitpunkt.
 *
 * Das ist die Stelle, an der die Zeitzonen-Rechnung im neuen Schichtmodell
 * stattfindet: Wer „Frühschicht ab 04:00" plant, meint 04:00 auf der Uhr an der
 * Wand — welcher UTC-Zeitpunkt das ist, hängt von der Jahreszeit ab. Früher
 * wurde diese Frage bei JEDER Auswertung neu gestellt; jetzt einmal beim Planen.
 *
 * @param datumIso Kalendertag in Berlin, YYYY-MM-DD
 * @param uhrzeit  Wanduhrzeit, "HH:MM"
 *
 * Zwei Nächte im Jahr sind besonders — beide sind unten in
 * `berlin-zeitpunkt.test.ts` festgeschrieben:
 *
 * - **Ende Oktober** kommt eine Uhrzeit zweimal vor (02:00–03:00). Geliefert
 *   wird das ZWEITE Vorkommen, also schon Winterzeit — die sichere Seite: Endet
 *   eine Schicht dort, deckt sie die doppelte Stunde vollständig ab.
 * - **Ende März** fehlt eine Stunde (02:00–03:00 gibt es nicht). Eine Uhrzeit
 *   aus dieser Lücke wird zu dem Moment, den sie ohne Umstellung gehabt hätte;
 *   die Wanduhr zeigt dann eine Stunde später (geplant 02:30 → 03:30). Eine
 *   geplante Schicht darf an dieser Nacht nicht platzen. Bei ELIS liegt ohnehin
 *   keine Schichtgrenze in der Lücke.
 */
export function berlinZeitpunkt(datumIso: string, uhrzeit: string): Date {
  const [jahr, monat, tag] = datumIso.split('-').map(Number);
  const [stunde, minute] = uhrzeit.split(':').map(Number);
  const alsWaereEsUtc = Date.UTC(jahr, monat - 1, tag, stunde, minute, 0, 0);

  // Erste Näherung mit dem Offset, der am gesuchten Zeitpunkt ungefähr gilt …
  const naeherung = new Date(alsWaereEsUtc - getBerlinOffset(new Date(alsWaereEsUtc)));
  // … dann mit dem Offset, der dort tatsächlich gilt. In den Umstellungsnächten
  // sind beide verschieden, weil der Sprung dazwischenliegt; ein zweiter
  // Durchgang genügt, solange die Uhr um höchstens eine Stunde springt.
  return new Date(alsWaereEsUtc - getBerlinOffset(naeherung));
}

/** Wanduhrzeit "HH:MM" eines Zeitpunkts in Berlin. */
export function formatBerlinZeit(date: Date): string {
  return date.toLocaleTimeString('de-DE', {
    timeZone: BERLIN_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/**
 * Formatiert ein Datum als YYYY-MM-DD in Berlin-Zeit
 */
export function formatBerlinDate(date: Date): string {
  return date.toLocaleDateString('sv-SE', { timeZone: BERLIN_TZ });
}

/**
 * Gibt den aktuellen Tag in Berlin als YYYY-MM-DD zurück
 */
export function getTodayBerlin(now?: Date): string {
  return formatBerlinDate(now || new Date());
}

/**
 * Gibt ein Datum N Tage in der Vergangenheit als YYYY-MM-DD zurück
 */
export function getDaysAgoBerlin(days: number, now?: Date): string {
  const ref = now || new Date();
  const date = new Date(ref.getTime() - days * 24 * 60 * 60 * 1000);
  return formatBerlinDate(date);
}

// =============================================================================
// DAY RANGE (Half-Open [start, end))
// =============================================================================

/**
 * Gibt die Tagesgrenzen für ein Datum in Europe/Berlin zurück
 *
 * @param date - Referenzdatum (default: jetzt)
 * @returns { start, end } - start = 00:00 Berlin, end = 00:00 Berlin nächster Tag
 *
 * WICHTIG: Half-open [start, end) - Query: { gte: start, lt: end }
 *
 * Legacy Evidence: /opt/wash-ops/src/lib/dates.ts:49-71
 */
export function getBerlinDayRange(date?: Date): DateRange {
  const now = date || new Date();

  // Formatiere das Datum in Berlin-Zeit
  const berlinDateStr = now.toLocaleDateString('sv-SE', { timeZone: BERLIN_TZ });
  const [year, month, day] = berlinDateStr.split('-').map(Number);

  // KRITISCH: Offset für START und END separat berechnen (DST-Wechsel!)
  // Start: 00:00 Berlin dieses Tages
  const startRefUtc = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
  const startOffset = getBerlinOffset(startRefUtc);
  const start = new Date(startRefUtc.getTime() - startOffset);

  // End: 00:00 Berlin nächster Tag (exklusiv)
  const endRefUtc = new Date(Date.UTC(year, month - 1, day + 1, 0, 0, 0));
  const endOffset = getBerlinOffset(endRefUtc);
  const end = new Date(endRefUtc.getTime() - endOffset);

  return { start, end };
}

/**
 * Parst einen YYYY-MM-DD String und gibt die Tagesgrenzen in Berlin zurück
 */
export function parseBerlinDateRange(dateStr: string): DateRange {
  const [year, month, day] = dateStr.split('-').map(Number);
  // Mittag um Timezone-Edge-Cases zu vermeiden
  const refDate = new Date(year, month - 1, day, 12, 0, 0);
  return getBerlinDayRange(refDate);
}

/**
 * "Gültig ab" (inklusives Kalenderdatum, YYYY-MM-DD) → gespeicherter
 * Zeitpunkt: 00:00:00.000 Berlin dieses Tages.
 *
 * Vorher wurde new Date('YYYY-MM-DD') = 00:00 UTC gespeichert — damit galt
 * die Regel erst ab 01:00/02:00 Berlin des angezeigten Tages (Review NW1).
 */
export function validityStartFromBerlinDate(dateStr: string): Date {
  return parseBerlinDateRange(dateStr).start;
}

/**
 * "Gültig bis" (inklusives Kalenderdatum, YYYY-MM-DD) → gespeicherter
 * Zeitpunkt: 23:59:59.999 Berlin dieses Tages.
 *
 * Der Matcher prüft exklusiv (timestamp > validUntil) — mit diesem Wert gilt
 * der letzte Tag vollständig. Bewusst NICHT 00:00 des Folgetags: So zeigt
 * formatBerlinDateISO/Anzeige weiterhin denselben Kalendertag und der
 * Formular-Roundtrip (laden → speichern) bleibt stabil — auch für
 * Alt-Bestände, die noch als 00:00 UTC gespeichert sind (Review NW1:
 * vorher war der letzte angezeigte Gültigkeitstag ab 01:00/02:00 Berlin
 * ausgeschlossen — die Samstagsregel-Falle vom 05.09.2026).
 */
export function validityEndFromBerlinDate(dateStr: string): Date {
  return new Date(parseBerlinDateRange(dateStr).end.getTime() - 1);
}

/**
 * Gibt die UTC-Grenzen für einen Datumsbereich zurück (Berlin)
 *
 * @param startStr - Start YYYY-MM-DD
 * @param endStr - End YYYY-MM-DD (inklusiv - wird zu exklusivem end+1 Tag)
 */
export function getDateRangeBounds(startStr: string, endStr: string): DateRange {
  const startRange = parseBerlinDateRange(startStr);
  const endRange = parseBerlinDateRange(endStr);
  return { start: startRange.start, end: endRange.end };
}

// =============================================================================
// ISO WEEK (Europe/Berlin)
// =============================================================================

/**
 * Berechnet die ISO-Kalenderwochennummer für ein Datum (Europe/Berlin)
 */
export function getISOWeekNumber(date: Date): number {
  const berlinDateStr = date.toLocaleDateString('sv-SE', { timeZone: BERLIN_TZ });
  const [year, month, day] = berlinDateStr.split('-').map(Number);

  const d = new Date(Date.UTC(year, month - 1, day));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

/**
 * Berechnet das ISO-Wochenjahr für ein Datum (Europe/Berlin)
 */
export function getISOWeekYear(date: Date): number {
  const berlinDateStr = date.toLocaleDateString('sv-SE', { timeZone: BERLIN_TZ });
  const [year, month, day] = berlinDateStr.split('-').map(Number);

  const d = new Date(Date.UTC(year, month - 1, day));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  return d.getUTCFullYear();
}

/**
 * Gibt Start (Mo) und Ende (So) einer ISO-Kalenderwoche als YYYY-MM-DD zurück
 */
export function getISOWeekBounds(year: number, week: number): { start: string; end: string } {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4DayOfWeek = jan4.getUTCDay() || 7;

  const mondayKW1 = new Date(jan4);
  mondayKW1.setUTCDate(jan4.getUTCDate() - jan4DayOfWeek + 1);

  const monday = new Date(mondayKW1);
  monday.setUTCDate(mondayKW1.getUTCDate() + (week - 1) * 7);

  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);

  return {
    start: formatBerlinDate(monday),
    end: formatBerlinDate(sunday),
  };
}

// =============================================================================
// MONTH (Europe/Berlin)
// =============================================================================

/**
 * Gibt den letzten Tag eines Monats als YYYY-MM-DD zurück
 * @param month - 1-indexed (1=Januar, 12=Dezember)
 */
export function getLastDayOfMonth(year: number, month: number): string {
  const lastDay = new Date(year, month, 0);
  return formatBerlinDate(lastDay);
}

/**
 * Gibt den ersten Tag eines Monats als YYYY-MM-DD zurück
 */
export function getFirstDayOfMonth(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}-01`;
}

// =============================================================================
// QUICK PICKERS
// =============================================================================

const MONTH_NAMES_DE = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

function formatDE(dateStr: string): string {
  const [y, m, d] = dateStr.split('-');
  return `${d}.${m}.${y}`;
}

/**
 * ZENTRALE QUICK PICKER FUNKTION
 *
 * @param picker - Quick Picker Typ
 * @param now - Referenzzeitpunkt (default: jetzt)
 * @returns BusinessDateRange mit Half-Open [start, end)
 *
 * Legacy Evidence: /opt/wash-ops/src/lib/dates.ts:294-431
 */
export function getQuickPickerRange(picker: QuickPicker, now?: Date): BusinessDateRange {
  const refDate = now || new Date();
  const todayStr = formatBerlinDate(refDate);

  switch (picker) {
    case 'today': {
      const { start, end } = parseBerlinDateRange(todayStr);
      return {
        start,
        end,
        startDate: todayStr,
        endDate: todayStr,
        label: `Heute (${formatDE(todayStr)})`,
        isPartial: true,
      };
    }

    case 'yesterday': {
      const yesterdayStr = getDaysAgoBerlin(1, refDate);
      const { start, end } = parseBerlinDateRange(yesterdayStr);
      return {
        start,
        end,
        startDate: yesterdayStr,
        endDate: yesterdayStr,
        label: `Gestern (${formatDE(yesterdayStr)})`,
        isPartial: false,
      };
    }

    case 'dayBefore': {
      const dayBeforeStr = getDaysAgoBerlin(2, refDate);
      const { start, end } = parseBerlinDateRange(dayBeforeStr);
      return {
        start,
        end,
        startDate: dayBeforeStr,
        endDate: dayBeforeStr,
        label: `Vorgestern (${formatDE(dayBeforeStr)})`,
        isPartial: false,
      };
    }

    case 'thisWeek': {
      const year = getISOWeekYear(refDate);
      const week = getISOWeekNumber(refDate);
      const bounds = getISOWeekBounds(year, week);
      const { start } = parseBerlinDateRange(bounds.start);
      const { end } = parseBerlinDateRange(todayStr);
      return {
        start,
        end,
        startDate: bounds.start,
        endDate: todayStr,
        label: `KW ${week} (${formatDE(bounds.start)} - ${formatDE(todayStr)})`,
        isPartial: true,
      };
    }

    case 'lastWeek': {
      const lastWeekDate = new Date(refDate.getTime() - 7 * 24 * 60 * 60 * 1000);
      const year = getISOWeekYear(lastWeekDate);
      const week = getISOWeekNumber(lastWeekDate);
      const bounds = getISOWeekBounds(year, week);
      const { start, end } = getDateRangeBounds(bounds.start, bounds.end);
      return {
        start,
        end,
        startDate: bounds.start,
        endDate: bounds.end,
        label: `KW ${week} (${formatDE(bounds.start)} - ${formatDE(bounds.end)})`,
        isPartial: false,
      };
    }

    case 'thisMonth': {
      const berlinDateStr = refDate.toLocaleDateString('sv-SE', { timeZone: BERLIN_TZ });
      const [year, month] = berlinDateStr.split('-').map(Number);
      const firstDay = getFirstDayOfMonth(year, month);
      const { start } = parseBerlinDateRange(firstDay);
      const { end } = parseBerlinDateRange(todayStr);
      return {
        start,
        end,
        startDate: firstDay,
        endDate: todayStr,
        label: `${MONTH_NAMES_DE[month - 1]} ${year}`,
        isPartial: true,
      };
    }

    case 'lastMonth': {
      const berlinDateStr = refDate.toLocaleDateString('sv-SE', { timeZone: BERLIN_TZ });
      const [currentYear, currentMonth] = berlinDateStr.split('-').map(Number);
      const lastMonthDate = new Date(currentYear, currentMonth - 2, 1);
      const year = lastMonthDate.getFullYear();
      const month = lastMonthDate.getMonth() + 1;
      const firstDay = getFirstDayOfMonth(year, month);
      const lastDay = getLastDayOfMonth(year, month);
      const { start, end } = getDateRangeBounds(firstDay, lastDay);
      return {
        start,
        end,
        startDate: firstDay,
        endDate: lastDay,
        label: `${MONTH_NAMES_DE[month - 1]} ${year}`,
        isPartial: false,
      };
    }
  }
}

/**
 * Custom Range mit Validierung
 */
export function getCustomRange(startStr: string, endStr: string): BusinessDateRange {
  // Validierung: Start <= End
  const startDate = startStr <= endStr ? startStr : endStr;
  const endDate = startStr <= endStr ? endStr : startStr;
  const { start, end } = getDateRangeBounds(startDate, endDate);
  const isSingleDay = startDate === endDate;

  return {
    start,
    end,
    startDate,
    endDate,
    label: isSingleDay ? formatDE(startDate) : `${formatDE(startDate)} - ${formatDE(endDate)}`,
    isPartial: false,
  };
}

// =============================================================================
// DEBUG / INSPECTION
// =============================================================================

/**
 * Gibt alle Quick Picker Ranges als Debug-Info zurück
 */
export function getAllQuickPickerRanges(now?: Date): Record<QuickPicker, BusinessDateRange> {
  const pickers: QuickPicker[] = [
    'today',
    'yesterday',
    'thisWeek',
    'lastWeek',
    'thisMonth',
    'lastMonth',
  ];
  const result = {} as Record<QuickPicker, BusinessDateRange>;

  for (const picker of pickers) {
    result[picker] = getQuickPickerRange(picker, now);
  }

  return result;
}

/**
 * Formatiert eine DateRange für Debug-Ausgabe
 */
export function formatRangeForDebug(range: DateRange): {
  startISO: string;
  endISO: string;
  startBerlin: string;
  endBerlin: string;
  durationHours: number;
} {
  return {
    startISO: range.start.toISOString(),
    endISO: range.end.toISOString(),
    startBerlin: range.start.toLocaleString('de-DE', { timeZone: BERLIN_TZ }),
    endBerlin: range.end.toLocaleString('de-DE', { timeZone: BERLIN_TZ }),
    durationHours:
      Math.round(((range.end.getTime() - range.start.getTime()) / (1000 * 60 * 60)) * 100) / 100,
  };
}
